<script setup>
import ArgonHeader from "@/components/table/ArgonHeader.vue";
import { onMounted } from "vue";
import { argonTableStore } from "@/_store/argonTableStore";
import { computed, watch, ref } from "vue";
import ArgonInput from "@/components/ArgonInput.vue";
import ArgonButton from "@/components/ArgonButton.vue";

const emit = defineEmits([
  "onReload",
  "pageChanged",
  "inputValues",
  "update:selectedIds",
  "toggle-check-item",
  "toggle-check-all",
  "row-enter",
]);

const tableStore = argonTableStore();
const tableContainer = ref();
const genericTable = ref();

const props = defineProps({
  color: {
    type: String,
    default: "success",
  },
  size: {
    type: String,
    default: "md",
  },
  showInputs:{
    type: Boolean,
    default: false,
  },
  showCheckInput: {
    type: Boolean,
    default: false,
  },
  selectedIds: {
    type: Array,
    default() {
      return [];
    },
  },
  disableCheckInput: {
    type: Boolean,
    default: false,
  },
  rowIdField: {
    type: String,
    default: "_id",
  },
  emptyMessage: {
  type: String,
  default: "No hemos encontrado resultados",
},
  items: {
    type: Object,
    default() {
      return {
        total: 0,
        data: [],
      };
    },
  },
  headers: {
    type: Array,
    default() {
      return [];
    },
  },
  showPagination: {
    type: Boolean,
    default: false,
  },
  showLineSeparator:{
    type: Boolean,
    default: true
  },
  maxHeight: {
    type: Number,
    default: 220
  },
  minHeight: {
    type: Number,
    default: 220
  },
  height: {
    type: Number,
    default: null
  },
  bodyMinHeight: {
    type: Number,
    default: 0
  },
  fixedLayout: {
    type: Boolean,
    default: false
  }

});

const tableStyle = computed(() => {
  if (props.height !== null) {
    return {
      height: `${props.height}px`,
      maxHeight: `${props.height}px`,
      minHeight: `${props.height}px`,
    };
  }

  return {
    maxHeight: `${props.maxHeight}px`,
    minHeight: `${props.minHeight}px`,
  };
});

const bodyStyle = computed(() => {
  if (!props.bodyMinHeight) return {};
  return { minHeight: `${props.bodyMinHeight}px` };
});

let dataInputs = {
  postal_code: "",
  cutoff_time: ""
};

const selectedIdSet = computed(() => {
  return new Set((props.selectedIds || []).map((id) => String(id)));
});

const selectableItems = computed(() => {
  if (!props.items?.data || !Array.isArray(props.items.data)) return [];
  return props.items.data.filter((item, index) =>
    Boolean(resolveRowId(item, index))
  );
});

const allSelected = computed(() => {
  if (!props.showCheckInput || selectableItems.value.length === 0) return false;
  return selectableItems.value.every((item, index) =>
    selectedIdSet.value.has(resolveRowId(item, index))
  );
});

function resolveRowId(item, index) {
  const value = item?.[props.rowIdField];
  if (value === undefined || value === null || value === "") {
    return String(index);
  }
  return String(value);
}

function emitSelectedIds(nextIds) {
  emit("update:selectedIds", nextIds);
}

function toggleItem(item, index, checked) {
  const rowId = resolveRowId(item, index);
  const nextIds = new Set((props.selectedIds || []).map((id) => String(id)));

  if (checked) {
    nextIds.add(rowId);
  } else {
    nextIds.delete(rowId);
  }

  const normalizedIds = Array.from(nextIds);
  emitSelectedIds(normalizedIds);
  emit("toggle-check-item", {
    item,
    rowId,
    checked,
    selectedIds: normalizedIds,
  });
}

function toggleAll(checked) {
  const current = new Set((props.selectedIds || []).map((id) => String(id)));
  const idsInPage = selectableItems.value.map((item, index) =>
    resolveRowId(item, index)
  );

  if (checked) {
    idsInPage.forEach((id) => current.add(id));
  } else {
    idsInPage.forEach((id) => current.delete(id));
  }

  const normalizedIds = Array.from(current);
  emitSelectedIds(normalizedIds);
  emit("toggle-check-all", {
    checked,
    selectedIds: normalizedIds,
    items: selectableItems.value,
  });
}

function onReload() {
  displaySpinner(true);
  emit("onReload");
}

const nextPage = computed(() => {
  const maxPages = Math.ceil(props.items.total / 20);
  if (tableStore.pagination.page == maxPages) {
    return 1;
  }
  return tableStore.pagination.page + 1;
});

const previousPage = computed(() => {
  const maxPages = Math.ceil(props.items.total / 20);
  if (tableStore.pagination.page === 1) {
    return maxPages;
  }
  return tableStore.pagination.page - 1;
});

const pagination_description = computed(() => {
  const startPage = (tableStore.pagination.page - 1) * 20 + 1;
  let defaultTotalPage=props.items.total ? 20:0;
  const endPage = startPage + defaultTotalPage - 1;
  return `${startPage}-${endPage > props.items.total ? props.items.total : endPage}`;
});

function displaySpinner(show=false) {
  if (show) {
    let container = tableContainer.value
    let table = genericTable.value
    table.classList.add("overflow-hidden")
    container.classList.add('loading')
    container.style.setProperty('--size', '40px 40px');
    if (props.items.data.length <= 2 ) {
      container.style.setProperty('--top-position', '40%');
    }else{
      container.style.setProperty('--top-position', '0');
    }
    return
  }

  let container = tableContainer.value
    container.classList.remove('loading')


  let table = genericTable.value
  table.classList.remove("overflow-hidden")
}

function changePage(newPage) {
  tableStore.pagination.page = newPage;
  displaySpinner(true);
  emit("pageChanged", newPage);
}
function agregarExcepcion(){
  emit("inputValues", dataInputs);
}

watch(
  () => props.items,
  () => {
    displaySpinner();
  }
);

onMounted(() => {});

defineExpose({
  scrollToBottom: () => {
    if (genericTable.value) genericTable.value.scrollTop = genericTable.value.scrollHeight;
  },
});
</script>

<template>
  <div class="p-0">
    <div id="simple-table-container" ref="tableContainer" class="col-md-auto loading">
      <nav v-if="showPagination" aria-label="paginacion" class="d-flex justify-content-end align-items-center">
        <ul class="pagination m-0 pe-1">
          <li class="page-item">
            <a v-tooltip="'Refrescar'" class="page-link" @click="onReload">
              &#8634;
            </a>
          </li>
        </ul>
        <ul class="m-0 ps-1 pe-1">
          <span class="page color text-xs">{{ pagination_description }} / {{ items.total }}</span>
        </ul>
        <ul class="pagination justify-content-end m-0">
          <li class="page-item">
            <a class="page-link" @click="changePage(previousPage)">&lt;</a>
          </li>
          <li class="page-item">
            <a class="page-link" @click="changePage(nextPage)">&gt;</a>
          </li>
        </ul>
      </nav>
      <div id="simple-table" ref="genericTable" :style="tableStyle">
        <table
          class="table align-items-center mb-0"
          :class="[showLineSeparator ? '' : 'table-borderless', { 'table-layout-fixed': fixedLayout }]"
        >
          <thead class="sticky-top bg-white">
            <tr>
              <th
                v-if="showCheckInput"
                class="font-weight-bolder opacity-7 text-center align-middle px-2"
                style="width: 42px; min-width: 42px;"
              >
                <div class="my-auto form-check">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    :checked="allSelected"
                    :disabled="disableCheckInput || selectableItems.length === 0"
                    @change="toggleAll($event.target.checked)"
                  />
                </div>
              </th>
              <argon-header :headers="headers"></argon-header>
            </tr>
            <tr v-if="showInputs">
              <th v-if="showCheckInput"></th>
              <th>
                <argon-input v-model="dataInputs.postal_code" :max-length="5" type="number" />
              </th>
              <th>
                <argon-input v-model="dataInputs.cutoff_time" type="time" />
              </th>
              <th>
                <argon-button size="xs" color="primary" class="d-flex justify-content-center align-items-center" @click="agregarExcepcion">+</argon-button>
              </th>
            </tr>
          </thead>
          <tbody :style="bodyStyle">
            <template v-if="items && items.data && items.data.length > 0">
              <tr
                v-for="(item, index) in items.data"
                :key="item._id || resolveRowId(item, index)"
                @keydown.enter.prevent="emit('row-enter', item)"
              >
                <td
                  v-if="showCheckInput"
                  class="text-center align-middle px-2"
                  style="width: 42px; min-width: 42px;"
                >
                  <div class="my-auto form-check">
                    <input
                      class="form-check-input"
                      type="checkbox"
                      :checked="selectedIdSet.has(resolveRowId(item, index))"
                      :disabled="disableCheckInput"
                      @change="toggleItem(item, index, $event.target.checked)"
                    />
                  </div>
                </td>
                <slot :item="item"></slot>
              </tr>
            </template>
            <template v-else>
              <tr>
                <td :colspan="headers.length + (showCheckInput ? 1 : 0)">
                  <div class="col-lg-12 text-center py-2">
                    <span class="label-placeholder text-sm">
                    {{ emptyMessage }}
                    </span>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot class="text-end simple-table-foot" @keydown.enter.prevent="emit('row-enter', null)">
            <slot name="foot-table"></slot>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.color {
  color: #8091C5 !important;
}
#simple-table {
  overflow-y: auto;
}

.table-layout-fixed {
  table-layout: fixed;
}

.simple-table-foot {
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
}

.simple-table-foot,
.simple-table-foot tr:first-child td {
  border-top: 1px solid #dee2e6 !important;
  border-bottom: none !important;
}

.overflow-hidden {
  overflow: hidden;
}

#simple-table-container.loading{
  position: relative;
}

#simple-table-container.loading::after {
  position: absolute;
  top: var(--top-position);
  left: 0;
  right: 0;
  bottom: 0;
  // background-color: rgba(0, 0, 0, 0.046);
  background-image: url('../../assets/img/loading.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: var(--size);
  content: "";
}
.card-detail-layout .table tfoot{
  border-color: #dee2e6 !important;
}
</style>
